import { computed, ref } from 'vue'
import api from '@/services/api'
import { useNow } from '@/composables/game/time'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { type GiveawayPrize, GiveawayPrizeType, type GiveawayWinner } from '@/types/giveaway'
import { useConfig } from '@/composables/game/config'
import { toOrdinal } from '@/helpers/utils'
import { formatDuration } from '@/helpers/date'
import { useAuth } from '@/composables/auth'
import {
  CHALLENGE_PRIZE_TYPE,
  type GiveawayConfig,
  isPrizePoolCash,
  isPrizePoolNft,
  type PrizePoolAmount,
} from '@/types/shared/player-config'
import { getCardMedal, getPrizeTypeImage } from '@/composables/game/challenges'
import type { CardChallengeLeaderboardPlayer } from '@/types/leaderboard-player'

const playerTickets = useLocalStorage<number>(buildDropeeStorageKey('playerGiveawayTickets'), 0)
const formSubmitted = useLocalStorage<boolean>(buildDropeeStorageKey('giveawayFormSubmitted'), false)

const winners = ref<Array<GiveawayWinner> | undefined>(undefined)
const drawn = ref(false)

const userPosition = ref<number | null>(null)
const isWinner = computed(() => userPosition.value !== null)

const giveawayPreviousEndTimestamp = ref(0)
const totalTickets = ref(0)
const previousPlayerTickets = ref(0)
const previousTotalTickets = ref(0)
const previousGiveaway = ref<GiveawayConfig | null>(null)

export const useGiveaway = () => {
  const { now } = useNow()
  const { config } = useConfig()
  const { profile } = useAuth()

  const giveaway = computed(() => config.value.game.giveaway)
  const giveawayStartTimestamp = computed(() => giveaway.value?.from)
  const giveawayEndTimestamp = computed(() => giveaway.value?.to)
  const giveawayDrawTimestamp = computed(() =>
    giveawayEndTimestamp.value ? giveawayEndTimestamp.value + (24 * 3600) : null,
  )

  const countdown = computed(() => {
    if (!giveawayStartTimestamp.value || !giveawayEndTimestamp.value) {
      return 0
    }
    const startTimeDiff = giveawayStartTimestamp.value - now.value
    if (startTimeDiff > 0) {
      return 0
    }
    const timeDiff = giveawayEndTimestamp.value - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const drawCountdown = computed(() => {
    if (!giveawayDrawTimestamp.value) return 0
    const timeDiff = giveawayDrawTimestamp.value - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const hasWinners = computed(() => !!winners.value?.length)

  // Dynamic prize pools from config
  const prizePool = computed(() => giveaway.value?.prizePool ?? [])
  const previousPrizePool = computed(() => previousGiveaway.value?.prizePool ?? [])

  // Calculate total prize from prize pool
  const totalPrize = computed(() => {
    return prizePool.value.reduce((sum, prize) => {
      if (isPrizePoolCash(prize)) {
        return sum + prize.amount * (prize.toRank + 1 - prize.fromRank)
      }
      return sum
    }, 0)
  })

  const previousTotalPrize = computed(() => {
    return previousPrizePool.value.reduce((sum, prize) => {
      if (isPrizePoolCash(prize)) {
        return sum + prize.amount * (prize.toRank + 1 - prize.fromRank)
      }
      return sum
    }, 0)
  })

  // Helper to get prize type for display
  function getPrizeTypeForDisplay(prize: PrizePoolAmount, prizeType: CHALLENGE_PRIZE_TYPE | undefined): GiveawayPrizeType {
    if (isPrizePoolNft(prize)) {
      return GiveawayPrizeType.NFT
    }

    switch (prizeType) {
    case CHALLENGE_PRIZE_TYPE.USDT:
      return GiveawayPrizeType.USDT
    case CHALLENGE_PRIZE_TYPE.DROPEE:
      return GiveawayPrizeType.DROPEE
    case CHALLENGE_PRIZE_TYPE.BADGE:
      return GiveawayPrizeType.BADGE
    case CHALLENGE_PRIZE_TYPE.TON:
      return GiveawayPrizeType.TON
    default:
      return GiveawayPrizeType.TON
    }
  }

  // Map prize pool to GiveawayPrize list for display
  const prizeList = computed<GiveawayPrize[]>(() => {
    return prizePool.value.map((prize) => {
      const label = prize.fromRank === prize.toRank
        ? `${toOrdinal(prize.fromRank)} Place`
        : `${prize.fromRank} - ${prize.toRank}`

      if (isPrizePoolNft(prize)) {
        return {
          type: GiveawayPrizeType.NFT,
          label,
          nftMetadata: {
            name: prize.nft,
            lottie: prize.lottieUrl,
          },
        }
      }

      return {
        amount: isPrizePoolCash(prize) ? prize.amount : 0,
        type: getPrizeTypeForDisplay(prize, giveaway.value?.prizeType),
        label,
      }
    })
  })

  // Map previous prize pool to GiveawayPrize list for display
  const previousPrizeList = computed<GiveawayPrize[]>(() => {
    return previousPrizePool.value.map((prize) => {
      const label = prize.fromRank === prize.toRank
        ? `${toOrdinal(prize.fromRank)} Place`
        : `${prize.fromRank} - ${prize.toRank}`

      if (isPrizePoolNft(prize)) {
        return {
          type: GiveawayPrizeType.NFT,
          label,
          nftMetadata: {
            name: prize.nft,
            lottie: prize.lottieUrl,
          },
        }
      }

      return {
        amount: isPrizePoolCash(prize) ? prize.amount : 0,
        type: getPrizeTypeForDisplay(prize, previousGiveaway.value?.prizeType),
        label,
      }
    })
  })

  // Map prize pool to expanded list for winners display (one entry per rank)
  const prizeListForWinners = computed<GiveawayPrize[]>(() => {
    const result: GiveawayPrize[] = []
    for (const prize of prizePool.value) {
      const count = prize.toRank - prize.fromRank + 1

      if (isPrizePoolNft(prize)) {
        for (let i = 0; i < count; i++) {
          result.push({
            type: GiveawayPrizeType.NFT,
            nftMetadata: {
              name: prize.nft,
              lottie: prize.lottieUrl,
            },
          })
        }
      } else {
        for (let i = 0; i < count; i++) {
          result.push({
            amount: isPrizePoolCash(prize) ? prize.amount : 0,
            type: getPrizeTypeForDisplay(prize, giveaway.value?.prizeType),
          })
        }
      }
    }
    return result
  })

  // Map previous prize pool to expanded list for winners display (one entry per rank)
  const previousPrizeListForWinners = computed<GiveawayPrize[]>(() => {
    const result: GiveawayPrize[] = []
    for (const prize of previousPrizePool.value) {
      const count = prize.toRank - prize.fromRank + 1

      if (isPrizePoolNft(prize)) {
        for (let i = 0; i < count; i++) {
          result.push({
            type: GiveawayPrizeType.NFT,
            nftMetadata: {
              name: prize.nft,
              lottie: prize.lottieUrl,
            },
          })
        }
      } else {
        for (let i = 0; i < count; i++) {
          result.push({
            amount: isPrizePoolCash(prize) ? prize.amount : 0,
            type: getPrizeTypeForDisplay(prize, previousGiveaway.value?.prizeType),
          })
        }
      }
    }
    return result
  })

  // Prize type images
  const prizeTypeImage = computed(() => getPrizeTypeImage(giveaway.value?.prizeType))
  const previousPrizeTypeImage = computed(() => getPrizeTypeImage(previousGiveaway.value?.prizeType))

  // Formatted countdown string
  const formattedCountdown = computed(() => formatDuration(
    countdown.value,
    undefined,
    countdown.value > 3600 * 72,
  ))

  // Show LuckyRush button if active giveaway or finished within 24 hours
  const shouldShowLuckyRushButton = computed(() => {
    if (giveaway.value) return true

    const twentyFourHoursInSeconds = 24 * 60 * 60
    if (giveawayPreviousEndTimestamp.value > 0 &&
        now.value - giveawayPreviousEndTimestamp.value < twentyFourHoursInSeconds) {
      return true
    }

    return false
  })

  // Helper function to get amount for a specific rank
  function getAmountForRank(rank: number): number {
    const prize = prizePool.value.find(
      (p) => rank >= p.fromRank && rank <= p.toRank,
    )
    if (!prize || !isPrizePoolCash(prize)) return 0
    return prize.amount
  }

  // Helper function to get prize value for a position
  function getPrizeValueForPosition(position: number): number {
    return getAmountForRank(position)
  }

  // Helper function to get previous prize value for a position
  function getPreviousPrizeValueForPosition(position: number): number {
    const prize = previousPrizePool.value.find(
      (p) => position >= p.fromRank && position <= p.toRank,
    )
    if (!prize || !isPrizePoolCash(prize)) return 0
    return prize.amount
  }

  // Map winners to CardChallengeLeaderboardPlayer format
  const previousWinners = computed<CardChallengeLeaderboardPlayer[]>(() => {
    return (winners.value ?? []).map((w, i) => ({
      firstName: w.firstName,
      score: getAmountForRank(i + 1),
      photoUrl: w.photoUrl ?? '',
      isThisPlayer: !!profile.value.firstName && w.firstName === profile.value.firstName,
    }))
  })

  async function syncUserGiveawayTickets() {
    const currentTickets = await api.getGiveawayTickets()

    playerTickets.value = currentTickets.player
    totalTickets.value = currentTickets.total
  }

  async function syncGiveaway() {
    const [previousTickets, winnersState] = await Promise.all([
      api.getPreviousGiveawayTickets(),
      api.getRaffleWinners(),
      syncUserGiveawayTickets(),
    ])
    previousPlayerTickets.value = previousTickets.player
    previousTotalTickets.value = previousTickets.total
    giveawayPreviousEndTimestamp.value = previousTickets.previousGiveaway?.to || 0
    previousGiveaway.value = previousTickets.previousGiveaway
    userPosition.value = winnersState.position
    winners.value = winnersState.winners
    drawn.value = winnersState.currentGiveawayWinnersDrawn
  }

  return {
    countdown,
    drawCountdown,
    playerTickets,
    totalTickets,
    giveawayEndTimestamp,
    giveawayDrawTimestamp,
    giveaway,
    hasWinners,
    isWinner,
    syncGiveaway,
    syncUserGiveawayTickets,
    userPosition,
    winners,
    formSubmitted,
    giveawayPreviousEndTimestamp,
    previousPlayerTickets,
    previousTotalTickets,
    drawn,
    totalPrize,
    previousTotalPrize,
    prizeList,
    prizeListForWinners,
    previousPrizeList,
    previousPrizeListForWinners,
    prizeTypeImage,
    previousPrizeTypeImage,
    formattedCountdown,
    previousWinners,
    previousGiveaway,
    getAmountForRank,
    getPrizeValueForPosition,
    getPreviousPrizeValueForPosition,
    getPrizeTypeImage,
    getCardMedal,
    shouldShowLuckyRushButton,
  }
}
